import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { isDesktop } from "src/lib/desktop";
import { applyPwaUpdate } from "src/config/pwaClient";
import { forceRefreshAndReload } from "src/utils/runtimeAssetRecovery";
import {
  APP_UPDATE_AVAILABLE_EVENT,
  APP_UPDATE_RELOADING_EVENT,
  APP_UPDATE_REQUIRED_EVENT,
} from "src/utils/appUpdateEvents";
import { UpdateIcon, PrimaryButton, SecondaryButton } from "./AppUpdateChecker";

/**
 * Web update banner (non-blocking, fixed at the bottom).
 *
 * Three states:
 * - "available": the Service Worker detected a newly deployed build while the
 *   page is still healthy. The update is NOT applied automatically — the user
 *   keeps working and applies it explicitly, so unsaved form data is never
 *   wiped by a silent reload.
 * - "reloading": a missing chunk (deploy removed old build) broke the page and
 *   runtime asset recovery scheduled an auto-refresh. A short countdown tells
 *   the user what is happening; Cancel stops it and falls back to the manual
 *   "required" banner.
 * - "required": auto-recovery exhausted its attempts (or was cancelled). The
 *   page can't recover on its own, so the banner offers a cache-clearing
 *   refresh.
 *
 * Desktop shells use AppUpdateChecker (native updater) for proactive notices;
 * this banner still shows the chunk-recovery states there.
 */
export default function WebUpdateBanner() {
  const { t } = useTranslation();
  const [mode, setMode] = useState(null); // null | "available" | "reloading" | "required"
  const [countdown, setCountdown] = useState(0);

  useEffect(() => {
    const onAvailable = () => {
      // Desktop has its own native update prompt — avoid duplicate notices.
      if (isDesktop()) return;
      setMode((current) =>
        current === "reloading" || current === "required" ? current : "available",
      );
    };
    const onReloading = (event) => {
      // React owns the reload now — stop the vanilla fallback timer.
      event.preventDefault();
      setCountdown(event.detail?.countdownSeconds ?? 3);
      setMode("reloading");
    };
    const onRequired = () => setMode("required");
    window.addEventListener(APP_UPDATE_AVAILABLE_EVENT, onAvailable);
    window.addEventListener(APP_UPDATE_RELOADING_EVENT, onReloading);
    window.addEventListener(APP_UPDATE_REQUIRED_EVENT, onRequired);
    return () => {
      window.removeEventListener(APP_UPDATE_AVAILABLE_EVENT, onAvailable);
      window.removeEventListener(APP_UPDATE_RELOADING_EVENT, onReloading);
      window.removeEventListener(APP_UPDATE_REQUIRED_EVENT, onRequired);
    };
  }, []);

  useEffect(() => {
    if (mode !== "reloading") return undefined;
    if (countdown <= 0) {
      forceRefreshAndReload();
      return undefined;
    }
    const timer = setTimeout(() => setCountdown((c) => c - 1), 1000);
    return () => clearTimeout(timer);
  }, [mode, countdown]);

  if (!mode) return null;

  if (mode === "reloading") {
    return (
      <div
        id="web-update-banner"
        role="alert"
        className="fixed bottom-5 left-1/2 z-[100] flex w-[min(92vw,26rem)] -translate-x-1/2 items-start gap-3 rounded-xl shadow-2xl p-4"
        style={{ backgroundColor: "var(--white)", border: "1px solid var(--darkGray)" }}
      >
        <UpdateIcon />
        <div className="min-w-0 flex-1">
          <h3 className="m-0 text-[13px] font-bold" style={{ color: "var(--black)" }}>
            {t("webUpdate.reloadingTitle", "Updating to the new version")}
          </h3>
          <p className="m-0 mt-0.5 text-[12px] leading-relaxed" style={{ color: "var(--gray)" }}>
            {t("webUpdate.reloadingDescription", {
              defaultValue:
                "A new version was deployed. This page will refresh automatically in {{seconds}} seconds.",
              seconds: countdown,
            })}
          </p>
        </div>
        <SecondaryButton onClick={() => setMode("required")}>
          {t("webUpdate.cancel", "Cancel")}
        </SecondaryButton>
      </div>
    );
  }

  const isRequired = mode === "required";
  const refresh = () => (isRequired ? forceRefreshAndReload() : applyPwaUpdate());

  return (
    <div
      id="web-update-banner"
      role="alert"
      className="fixed bottom-5 left-1/2 z-[100] flex w-[min(92vw,26rem)] -translate-x-1/2 items-start gap-3 rounded-xl shadow-2xl p-4"
      style={{ backgroundColor: "var(--white)", border: "1px solid var(--darkGray)" }}
    >
      <UpdateIcon />
      <div className="min-w-0 flex-1">
        <h3 className="m-0 text-[13px] font-bold" style={{ color: "var(--black)" }}>
          {isRequired
            ? t("webUpdate.requiredTitle", "Update required")
            : t("webUpdate.availableTitle", "New version available")}
        </h3>
        <p className="m-0 mt-0.5 text-[12px] leading-relaxed" style={{ color: "var(--gray)" }}>
          {isRequired
            ? t(
                "webUpdate.requiredDescription",
                "Part of the page couldn't load because a new version was deployed. Refresh to continue.",
              )
            : t(
                "webUpdate.availableDescription",
                "A new version of Doitify is available. Refresh the page to get the latest version.",
              )}
        </p>
        <div className="mt-3 flex items-center gap-2">
          <PrimaryButton onClick={refresh}>{t("webUpdate.refresh", "Refresh")}</PrimaryButton>
          <SecondaryButton onClick={() => setMode(null)}>
            {t("appUpdate.later", "Later")}
          </SecondaryButton>
        </div>
      </div>
    </div>
  );
}
